import React, { useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import moment from "moment-timezone";
import { isMobile } from "react-device-detect";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import DashboardLayout from "../../../components/layout/DashboardLayout";
import ResourceList from "../../../components/resourceList/ResourceList";
import Input from "../../../components/input/Input";
import lpfrLicencesService from "../../../apis/services/admin/lpfrLicencesService";
import styles from "./LpfrLicencesScreen.module.scss";

const TZ = "Europe/Belgrade";
const PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 400;
const DAY_MS = 24 * 3600 * 1000;
// A device that does not reach the server for this long loses its lease.
const LEASE_DAYS = 30;
const UID_PATTERN = /^[A-Z0-9]{8}$/;

const HEADER_VALUES = [
  { label: "UID", key: "uid", sortable: false },
  { label: "OBVEZNIK", key: "businessName", sortable: false },
  { label: "LOKACIJA", key: "locationName", sortable: false },
  { label: "VERZIJA / OS", key: "version", sortable: false },
  { label: "POSLEDNJI KONTAKT", key: "lastSeenAt", sortable: false },
  { label: "STATUS", key: "status", sortable: false },
  { label: "VAŽI DO", key: "expiresAt", sortable: false },
  { label: "NAPOMENA", key: "note", sortable: false },
  { label: "AKCIJE", key: "actions", sortable: false, size: "lg" },
];

const STATUS_CONFIG = {
  active: { label: "Aktivna", className: "statusActive" },
  expired: { label: "Istekla", className: "statusExpired" },
  disabled: { label: "Isključena", className: "statusDisabled" },
};

const OS_LABELS = { windows: "Windows", darwin: "macOS", linux: "Linux" };

// BIGINT columns can arrive as strings; null stays null.
const toMs = (value) =>
  value === null || value === undefined || value === "" ? null : Number(value);

const effectiveStatus = (licence, now) => {
  if (licence.status === "disabled") return "disabled";
  const expiresAt = toMs(licence.expiresAt);
  if (expiresAt && now >= expiresAt) return "expired";
  return "active";
};

const isStale = (licence, now) => {
  const lastSeenAt = toMs(licence.lastSeenAt);
  return !lastSeenAt || now - lastSeenAt > LEASE_DAYS * DAY_MS;
};

const formatDateTime = (ms) => moment(ms).tz(TZ).format("DD.MM.YYYY HH:mm");
const formatDate = (ms) => moment(ms).tz(TZ).format("DD.MM.YYYY");

const formatRelative = (ms, now) => {
  const diff = Math.max(now - ms, 0);
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / DAY_MS);
  if (minutes < 1) return "upravo sada";
  if (minutes < 60) return `pre ${minutes} min`;
  if (hours < 24) return `pre ${hours} h`;
  if (days === 1) return "pre 1 dan";
  return `pre ${days} dana`;
};

// expiresAt = last millisecond of the chosen day in Belgrade time.
const endOfDayBelgrade = (yyyyMmDd) =>
  moment.tz(yyyyMmDd, "YYYY-MM-DD", TZ).endOf("day").valueOf();

const normalizeUidInput = (value) =>
  (value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 8);

const LpfrLicencesScreen = () => {
  const [licences, setLicences] = useState({
    data: [],
    total: 0,
    page: 1,
    perPage: PER_PAGE,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  // One open inline panel at a time: { uid, kind: 'disable'|'expiry'|'note', value }
  const [panel, setPanel] = useState(null);
  const [savingUid, setSavingUid] = useState(null);

  // Add / disable a card by UID (also for cards that never checked in).
  const [newUid, setNewUid] = useState("");
  const [newNote, setNewNote] = useState("");
  const [confirmNewDisable, setConfirmNewDisable] = useState(false);
  const [newSaving, setNewSaving] = useState(false);

  const requestId = useRef(0);
  const currentQuery = useRef({ search: "", page: 1 });
  const firstSearch = useRef(true);

  const load = useCallback(async (page, search) => {
    const id = ++requestId.current;
    currentQuery.current = { search, page };
    setLoading(true);
    try {
      const result = await lpfrLicencesService.fetchLicences({
        search,
        page,
        perPage: PER_PAGE,
      });
      if (id !== requestId.current) return; // a newer request superseded this one
      const total = result?.total ?? 0;
      const perPage = result?.perPage ?? PER_PAGE;
      setLicences({
        data: result?.rows ?? [],
        total,
        page: result?.page ?? page,
        perPage,
        totalPages: result?.totalPages ?? Math.ceil(total / perPage),
      });
    } catch (err) {
      if (id === requestId.current) toast.error(err.message);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, []);

  // Server-side search, debounced; the first run loads the list immediately.
  useEffect(() => {
    const delay = firstSearch.current ? 0 : SEARCH_DEBOUNCE_MS;
    firstSearch.current = false;
    const timer = setTimeout(() => {
      setPanel(null);
      load(1, searchTerm.trim());
    }, delay);
    return () => clearTimeout(timer);
  }, [searchTerm, load]);

  const onLoadPage = (page) => {
    setPanel(null);
    load(page, currentQuery.current.search);
  };

  const replaceRow = (row) => {
    setLicences((prev) => ({
      ...prev,
      data: prev.data.map((item) => (item.uid === row.uid ? row : item)),
    }));
  };

  const update = async (uid, changes, successMessage) => {
    if (savingUid) return;
    setSavingUid(uid);
    try {
      const row = await lpfrLicencesService.updateLicence(uid, changes);
      if (row) replaceRow(row);
      setPanel(null);
      toast.success(successMessage);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingUid(null);
    }
  };

  const onToggleStatus = (licence) => {
    if (licence.status === "disabled") {
      update(licence.uid, { status: "active" }, `Kartica ${licence.uid} je uključena`);
    } else {
      setPanel({ uid: licence.uid, kind: "disable" });
    }
  };

  const openExpiry = (licence) => {
    const expiresAt = toMs(licence.expiresAt);
    setPanel({
      uid: licence.uid,
      kind: "expiry",
      value: expiresAt ? moment(expiresAt).tz(TZ).format("YYYY-MM-DD") : "",
    });
  };

  const openNote = (licence) => {
    setPanel({ uid: licence.uid, kind: "note", value: licence.note || "" });
  };

  const submitNewUid = async (status) => {
    const uid = normalizeUidInput(newUid);
    if (!UID_PATTERN.test(uid)) {
      toast.error("UID mora imati tačno 8 znakova (A-Z, 0-9)");
      return;
    }
    if (newSaving) return;
    setNewSaving(true);
    try {
      const changes = { status };
      if (newNote.trim()) changes.note = newNote.trim();
      await lpfrLicencesService.updateLicence(uid, changes);
      toast.success(
        status === "disabled"
          ? `Kartica ${uid} je isključena`
          : `Kartica ${uid} je aktivna`
      );
      setNewUid("");
      setNewNote("");
      setConfirmNewDisable(false);
      // Show the card: a UID that never checked in sorts last in the list.
      if (searchTerm === uid) load(1, uid);
      else setSearchTerm(uid);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setNewSaving(false);
    }
  };

  const renderStatusPill = (status) => {
    const config = STATUS_CONFIG[status];
    return (
      <span className={classNames(styles.statusPill, styles[config.className])}>
        {config.label}
      </span>
    );
  };

  const renderBusiness = (licence) => (
    <div className={styles.stack}>
      <span>{licence.businessName || "—"}</span>
      {licence.tin && <span className={styles.muted}>PIB {licence.tin}</span>}
    </div>
  );

  // Fiscal device (L-PFR installation) that last checked in with this card.
  // SDF appends the free disk space to device names ("… {{12.3 GB}}").
  const renderLocation = (licence) => (
    <div className={styles.stack}>
      <span>{licence.locationName || "—"}</span>
      {licence.fiscalDevice && (
        <Link className={styles.muted} to={`/fiscal-devices/${licence.fiscalDevice.id}`}>
          {(licence.fiscalDevice.name || "Uređaj").replace(/\s*\{\{.*?\}\}/g, "")}
        </Link>
      )}
    </div>
  );

  const renderVersion = (licence) => (
    <div className={styles.stack}>
      <span>{licence.version || "—"}</span>
      {licence.os && (
        <span className={styles.muted}>{OS_LABELS[licence.os] || licence.os}</span>
      )}
    </div>
  );

  const renderLastSeen = (licence, now) => {
    const lastSeenAt = toMs(licence.lastSeenAt);
    return (
      <div className={styles.stack}>
        {lastSeenAt ? (
          <>
            <span>{formatRelative(lastSeenAt, now)}</span>
            <span className={styles.muted}>{formatDateTime(lastSeenAt)}</span>
          </>
        ) : (
          <span className={styles.muted}>Nikad se nije javio</span>
        )}
        {isStale(licence, now) && (
          <span className={styles.staleBadge}>Nije se javio &gt; {LEASE_DAYS} dana</span>
        )}
      </div>
    );
  };

  const renderExpiry = (licence, now) => {
    const expiresAt = toMs(licence.expiresAt);
    if (!expiresAt) return <span className={styles.muted}>Bez roka</span>;
    return (
      <span className={classNames(styles.nowrap, now >= expiresAt && styles.expiredText)}>
        {formatDate(expiresAt)}
      </span>
    );
  };

  const renderActions = (licence) => {
    const busy = savingUid === licence.uid;
    const disabled = licence.status === "disabled";
    return (
      <div className={styles.actions}>
        <button
          type="button"
          className={classNames(
            styles.actionButton,
            disabled ? styles.actionPrimary : styles.actionDanger
          )}
          disabled={busy}
          onClick={(e) => {
            e.stopPropagation();
            onToggleStatus(licence);
          }}
        >
          {disabled ? "Uključi" : "Isključi"}
        </button>
        <button
          type="button"
          className={styles.actionButton}
          disabled={busy}
          onClick={(e) => {
            e.stopPropagation();
            openExpiry(licence);
          }}
        >
          Važi do
        </button>
        <button
          type="button"
          className={styles.actionButton}
          disabled={busy}
          onClick={(e) => {
            e.stopPropagation();
            openNote(licence);
          }}
        >
          Napomena
        </button>
      </div>
    );
  };

  const renderPanel = (licence) => {
    if (!panel || panel.uid !== licence.uid) return null;
    const busy = savingUid === licence.uid;
    const close = () => setPanel(null);

    if (panel.kind === "disable") {
      return (
        <div className={classNames(styles.panel, styles.panelWarning)}>
          <p className={styles.panelText}>
            LPFR za karticu <strong>{licence.uid}</strong>
            {licence.businessName ? ` (${licence.businessName})` : ""} prestaje da
            izdaje račune posle sledeće provere licence. Karticu možete ponovo
            uključiti u bilo kom trenutku.
          </p>
          <div className={styles.panelButtons}>
            <button
              type="button"
              className={classNames(styles.actionButton, styles.actionDanger)}
              disabled={busy}
              onClick={() =>
                update(
                  licence.uid,
                  { status: "disabled" },
                  `Kartica ${licence.uid} je isključena`
                )
              }
            >
              {busy ? "Čuvanje..." : "Isključi karticu"}
            </button>
            <button type="button" className={styles.linkButton} onClick={close}>
              Odustani
            </button>
          </div>
        </div>
      );
    }

    if (panel.kind === "expiry") {
      const hasExpiry = !!toMs(licence.expiresAt);
      return (
        <div className={styles.panel}>
          <p className={styles.panelText}>
            Licenca za karticu <strong>{licence.uid}</strong> važi do kraja
            izabranog dana (beogradsko vreme).
          </p>
          <div className={styles.panelButtons}>
            <input
              type="date"
              className={styles.panelInput}
              value={panel.value}
              min={moment().tz(TZ).format("YYYY-MM-DD")}
              onChange={(e) => setPanel({ ...panel, value: e.target.value })}
            />
            <button
              type="button"
              className={classNames(styles.actionButton, styles.actionPrimary)}
              disabled={busy || !panel.value}
              onClick={() =>
                update(
                  licence.uid,
                  { expiresAt: endOfDayBelgrade(panel.value) },
                  `Rok za karticu ${licence.uid} je sačuvan`
                )
              }
            >
              {busy ? "Čuvanje..." : "Sačuvaj"}
            </button>
            {hasExpiry && (
              <button
                type="button"
                className={styles.actionButton}
                disabled={busy}
                onClick={() =>
                  update(
                    licence.uid,
                    { expiresAt: null },
                    `Rok za karticu ${licence.uid} je uklonjen`
                  )
                }
              >
                Ukloni rok
              </button>
            )}
            <button type="button" className={styles.linkButton} onClick={close}>
              Odustani
            </button>
          </div>
        </div>
      );
    }

    // note
    return (
      <div className={styles.panel}>
        <div className={styles.panelButtons}>
          <input
            type="text"
            className={classNames(styles.panelInput, styles.panelInputWide)}
            value={panel.value}
            maxLength={255}
            placeholder="Napomena"
            autoFocus
            onChange={(e) => setPanel({ ...panel, value: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter")
                update(licence.uid, { note: panel.value }, "Napomena je sačuvana");
              if (e.key === "Escape") close();
            }}
          />
          <button
            type="button"
            className={classNames(styles.actionButton, styles.actionPrimary)}
            disabled={busy}
            onClick={() =>
              update(licence.uid, { note: panel.value }, "Napomena je sačuvana")
            }
          >
            {busy ? "Čuvanje..." : "Sačuvaj"}
          </button>
          <button type="button" className={styles.linkButton} onClick={close}>
            Odustani
          </button>
        </div>
      </div>
    );
  };

  const renderRow = (licence, index) => {
    const now = Date.now();
    const status = effectiveStatus(licence, now);

    if (isMobile) {
      const fields = [
        ["Obveznik", renderBusiness(licence)],
        ["Lokacija", renderLocation(licence)],
        ["Verzija / OS", renderVersion(licence)],
        ["Poslednji kontakt", renderLastSeen(licence, now)],
        ["Važi do", renderExpiry(licence, now)],
        ["Napomena", licence.note || "—"],
      ];
      return (
        <div className={styles.card} key={licence.uid}>
          <div className={styles.cardHeader}>
            <span className={styles.uid} title={licence.installationId || undefined}>
              {licence.uid}
            </span>
            {renderStatusPill(status)}
          </div>
          {fields.map(([label, value]) => (
            <div className={styles.rowCard} key={label}>
              <p className={styles.rowCardTitle}>{label}</p>
              <div className={styles.rowCardValue}>{value}</div>
            </div>
          ))}
          {renderActions(licence)}
          {renderPanel(licence)}
        </div>
      );
    }

    return (
      <div key={licence.uid}>
        <div
          className={classNames(
            styles.row,
            index % 2 === 0 ? styles.even : styles.odd
          )}
        >
          <div className={styles.cell}>
            <span className={styles.uid} title={licence.installationId || undefined}>
              {licence.uid}
            </span>
          </div>
          <div className={styles.cell}>{renderBusiness(licence)}</div>
          <div className={styles.cell}>{renderLocation(licence)}</div>
          <div className={styles.cell}>{renderVersion(licence)}</div>
          <div className={styles.cell}>{renderLastSeen(licence, now)}</div>
          <div className={styles.cell}>{renderStatusPill(status)}</div>
          <div className={styles.cell}>
            {renderExpiry(licence, now)}
          </div>
          <div className={styles.cell}>
            <span className={styles.note}>{licence.note || "—"}</span>
          </div>
          <div className={classNames(styles.cell, styles.cellLg)}>
            {renderActions(licence)}
          </div>
        </div>
        {renderPanel(licence)}
      </div>
    );
  };

  const newUidValid = UID_PATTERN.test(newUid);

  return (
    <DashboardLayout>
      <div className={classNames(styles.content, isMobile && styles.contentMobile)}>
        <div className={styles.topPart}>
          <div className={styles.header}>
            <p className={isMobile ? styles.titleMobile : styles.title}>LPFR licence</p>
          </div>
          <div className={styles.flexEnd}>
            <div className={styles.searchWrapper}>
              <Input
                onChange={setSearchTerm}
                value={searchTerm}
                placeholder="Pretraga: UID, PIB, obveznik, lokacija"
              />
            </div>
          </div>
        </div>

        <div className={styles.addCard}>
          <p className={styles.addTitle}>Dodaj / isključi karticu po UID</p>
          <p className={styles.muted}>
            Radi i za karticu koja se još nije javila serveru. Uređaj koji se ne
            javi {LEASE_DAYS} dana gubi licencu.
          </p>
          <div className={styles.addForm}>
            <input
              type="text"
              className={classNames(styles.panelInput, styles.uidInput)}
              value={newUid}
              placeholder="UID kartice"
              autoCapitalize="characters"
              spellCheck={false}
              onChange={(e) => {
                setNewUid(normalizeUidInput(e.target.value));
                setConfirmNewDisable(false);
              }}
            />
            <input
              type="text"
              className={classNames(styles.panelInput, styles.panelInputWide)}
              value={newNote}
              maxLength={255}
              placeholder="Napomena (opciono)"
              onChange={(e) => setNewNote(e.target.value)}
            />
            <button
              type="button"
              className={classNames(styles.actionButton, styles.actionPrimary)}
              disabled={!newUidValid || newSaving}
              onClick={() => submitNewUid("active")}
            >
              Dodaj kao aktivnu
            </button>
            <button
              type="button"
              className={classNames(styles.actionButton, styles.actionDanger)}
              disabled={!newUidValid || newSaving}
              onClick={() => setConfirmNewDisable(true)}
            >
              Isključi
            </button>
          </div>
          {confirmNewDisable && newUidValid && (
            <div className={classNames(styles.panel, styles.panelWarning)}>
              <p className={styles.panelText}>
                LPFR za karticu <strong>{newUid}</strong> prestaje da izdaje račune
                posle sledeće provere licence.
              </p>
              <div className={styles.panelButtons}>
                <button
                  type="button"
                  className={classNames(styles.actionButton, styles.actionDanger)}
                  disabled={newSaving}
                  onClick={() => submitNewUid("disabled")}
                >
                  {newSaving ? "Čuvanje..." : "Isključi karticu"}
                </button>
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={() => setConfirmNewDisable(false)}
                >
                  Odustani
                </button>
              </div>
            </div>
          )}
        </div>

        <p className={styles.summary}>
          Ukupno: {licences.total}
          {licences.totalPages > 1 &&
            ` · strana ${licences.page} od ${licences.totalPages}`}
        </p>

        <div className={classNames(styles.listWrapper, isMobile && styles.listWrapperMobile)}>
          <ResourceList
            resource={{
              data: licences.data,
              total: licences.total,
              page: licences.page,
              perPage: licences.perPage,
              attempt: loading,
            }}
            headerValues={HEADER_VALUES}
            onLoadPage={onLoadPage}
            rowRender={renderRow}
            showLoader={true}
          />
          {!loading && licences.data.length === 0 && (
            <p className={styles.empty}>
              {searchTerm ? "Nema licenci za zadatu pretragu." : "Nema licenci."}
            </p>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default LpfrLicencesScreen;
